import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Droplets, Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative border-r border-white/10">
        <img src="https://images.unsplash.com/photo-1605164599894-ca98960d41b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxjYXIlMjB3YXNoJTIwd2F0ZXIlMjBzcGxhc2glMjBuZW9ufGVufDB8fHx8MTc4NDI1MTIxMnww&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative p-12 h-full flex flex-col justify-end">
          <div className="overline text-volt mb-2">$49.95 AUD / plate / month</div>
          <h2 className="font-head font-black text-5xl uppercase tracking-tight leading-none">Join The<br />Club.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 bg-volt flex items-center justify-center"><Droplets className="w-5 h-5 text-white" /></div>
            <span className="font-head font-extrabold text-xl tracking-tight uppercase">Earlwood</span>
          </Link>
          <div className="overline text-volt mb-2">Create Account</div>
          <h1 className="font-head font-extrabold text-3xl uppercase tracking-tight mb-8">Sign Up</h1>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline text-muted-foreground block mb-2">Full Name</label>
              <input data-testid="register-name-input" required value={form.name} onChange={set("name")}
                className="w-full bg-input border border-white/10 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-volt" />
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-2">Email</label>
              <input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")}
                className="w-full bg-input border border-white/10 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-volt" />
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-2">Mobile Number</label>
              <input data-testid="register-phone-input" type="tel" required value={form.phone} onChange={set("phone")} placeholder="0412 345 678"
                className="w-full bg-input border border-white/10 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-volt" />
              <p className="text-xs text-muted-foreground mt-1.5">We text renewal reminders here. AU mobile or +country format.</p>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-2">Password</label>
              <input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")}
                className="w-full bg-input border border-white/10 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-volt" />
            </div>
            {error && <div data-testid="register-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-4 py-3">{error}</div>}
            <button data-testid="register-submit-btn" disabled={loading} type="submit"
              className="w-full bg-volt text-white py-3 font-head font-bold uppercase tracking-wide hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create Account
            </button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            Already a member? <Link to="/login" data-testid="register-to-login" className="text-volt hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
